"use client";

import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from "react";
import { useIdentity } from "@/lib/api/session";
import { usePlatformEvents, type PlatformMessage } from "@/lib/chat/use-platform-events";
import { useHandleKycRequired } from "@/lib/kyc/hooks";
import {
  applyTransactionToast,
  createTransactionToasts,
  notificationEffect,
  transactionToast,
  type AccountNotification,
  type TransactionMessage,
  type TransactionToasts,
} from "@/lib/wallet/realtime-toasts";
import { useToasts, type ToastInput } from "@/components/ui/Toast";
import type { IconName } from "@/components/ui/Icon";

/* The platform's realtime stream, for every signed-in page — not only while
   the chat is open. The whitelabel's MainLayout subscribes to the account's
   transactions and notifications app-wide and turns them into toasts, so a
   deposit that lands while the player is in a game still says so; this is
   that, on the template's toasts.

   One connection: the chat panel listens through this provider rather than
   opening a second socket of its own (usePlatformEventsListener). The money
   queries are invalidated on every event by usePlatformEvents itself. */

type Context = {
  /* True while this provider holds the connection, so a consumer that would
     otherwise open its own does not. */
  active: boolean;
  subscribe: (listener: () => void) => () => void;
  /* "Deposit pending..." after a wallet payment, cleared by the transfer's
     first settled event — the whitelabel's showPendingToast with no id. */
  showPendingTransaction: (message: string) => void;
};

const PlatformEventsContext = createContext<Context>({
  active: false,
  subscribe: () => () => {},
  showPendingTransaction: () => {},
});

export const usePlatformEventsContext = () => useContext(PlatformEventsContext);

const ICON: Record<ToastInput["tone"], IconName> = {
  success: "check-circle",
  info: "info",
  error: "info",
};

export function PlatformEventsProvider({ children }: { children: ReactNode }) {
  const { identity, isSignedIn } = useIdentity();
  const accountId = identity?.id ?? null;
  const active = isSignedIn && accountId !== null;

  const { push, dismiss } = useToasts();
  const client = useQueryClient();
  const handleKycRequired = useHandleKycRequired();

  const transactions = useRef<TransactionToasts | null>(null);
  if (transactions.current === null) {
    transactions.current = createTransactionToasts({
      push: (toast) => push({ ...toast, icon: ICON[toast.tone] }),
      dismiss,
    });
  }

  const listeners = useRef(new Set<() => void>());
  const subscribe = useCallback((listener: () => void) => {
    listeners.current.add(listener);
    return () => {
      listeners.current.delete(listener);
    };
  }, []);

  const kycRef = useRef(handleKycRequired);
  kycRef.current = handleKycRequired;

  const onMessage = useCallback(
    ({ topic, name, data }: PlatformMessage) => {
      if (!accountId || typeof data !== "object" || data === null) return;

      if (topic === `accounts:${accountId}:transactions` && name === "transaction") {
        const message = data as TransactionMessage;
        const toast = transactionToast(message);

        if (toast && transactions.current) applyTransactionToast(transactions.current, toast);

        /* A first deposit changes the profile's hasDeposit, which the
           whitelabel reads again here. */
        if (message.kind === "transfer" && message.type === "credit" && message.status === "success") {
          void client.invalidateQueries({ queryKey: ["profile"] });
        }

        return;
      }

      if (topic === `accounts:${accountId}:notifications` && name === "account-notification") {
        const effect = notificationEffect(data as AccountNotification);

        if (!effect) return;

        if ("kycRequired" in effect) {
          kycRef.current();
          return;
        }

        push({ ...effect, icon: ICON[effect.tone] });
      }
    },
    [accountId, client, push],
  );

  usePlatformEvents({
    accountId,
    enabled: active,
    onMessage,
    onEvent: () => {
      for (const listener of listeners.current) listener();
    },
  });

  const showPendingTransaction = useCallback((message: string) => transactions.current?.pending(message), []);

  const value = useMemo(
    () => ({ active, subscribe, showPendingTransaction }),
    [active, subscribe, showPendingTransaction],
  );

  return <PlatformEventsContext.Provider value={value}>{children}</PlatformEventsContext.Provider>;
}

/* For a component that wants to know when anything arrives (the chat refreshes
   its feed): listens on the shell's connection. */
export const usePlatformEventsListener = (listener: () => void) => {
  const { subscribe } = usePlatformEventsContext();
  const ref = useRef(listener);
  ref.current = listener;

  useEffect(() => subscribe(() => ref.current()), [subscribe]);
};
